/**
 * This module is for producers and factory functions that return producers for the produce method of the immer library.
 * Producers themselves are impure by design, although the entire ecosystem is pure. For that reason, and because it
 * aligns better with their purpose, these should be tested as pure functions along with produce.
 */

import { findIndex } from 'lodash';
import * as K from 'common/constants';
import { LogEntryEdge } from 'common/graphqlTypes';

// TODO: Find a type for draft
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Producer = (draft: any) => void;

/**
 * Factory function which creates a producer to update the list tracking cursors for pagination. The returned producer
 * should only mutate the list if the index has not yet been set.
 *
 * @param cursor              new cursor to add to the list
 * @param paginationIndex     the index for the block of log lines this LRQ result represents in the abstract model of
 *                            log line blocks for pagination
 *
 * @return                    producer function to update the list of cursors
 */
export const updateCursors =
  (cursor: LogEntryEdge | null, paginationIndex: number): Producer =>
  (cursors: Array<LogEntryEdge>): void => {
    if (cursor && !cursors[paginationIndex]) {
      cursors[paginationIndex] = cursor;
    }
  };

/**
 * Factory function which creates a producer to update the current list of log ids (continuation tokens) based on the
 * new ids in a LogEntryResult from the server.
 *
 * - If currentIds already contains all of the ids in newIds, no change is made to currentIds.
 * - If currentIds contains only some of the ids in newIds, then currentIds is mutated such that all of the newIds are
 *   spliced on to currentIds overwriting duplicates.
 * - If currentIds contains none of the ids in newIds, the new ids will be spliced on to the end of the array.
 *
 * Note that this function operates under two important assumptions:
 *
 *  1) Log events from the server will show up in the correct order
 *  2) While new events could appear in subsequent results from the server (if we are searching an unfrozen epoch for
 *  example), results can't "go missing"; If we have previously seen ['a', 'b', 'c'], we could see ['a', 'b', 'z', 'c']
 *  in a subsequent result, but we won't ever see ['a', 'c'].
 *
 * Examples:
 *
 * newIds       ['a', 'b', 'c']
 * currentIds   []
 *      |
 *      v
 * ['a', 'b', 'c']
 *
 * newIds       ['c', 'd', 'e']
 * currentIds   ['a', 'b', 'c']
 *      |
 *      v
 * ['a', 'b', 'c', 'd', 'e']
 *
 * newIds       ['b', 'c', 'd', 'e']
 * currentIds   ['a']
 *      |
 *      v
 * ['a', 'b', 'c', 'd', 'e']
 *
 * @param newIds        Log ids (continuation tokens) returned by the server; some, all, or none of which may already
 *                      be included in the current ids in the cache
 *
 * @return              producer function to update the current ids based on the enclosed parameters.
 */
export const updateLogIdList =
  (newIds: ReadonlyArray<string>): Producer =>
  (currentIds: Array<string>): void => {
    // First try to find a matching overwrite index
    let start = findIndex(currentIds, (id) => newIds.includes(id));
    if (start === -1) {
      // otherwise, just start at the end of the list
      start = currentIds.length;
    }

    // overwrite everything from the start to the end of the list
    const deleteCount = currentIds.length - start;
    currentIds.splice(start, deleteCount, ...newIds);
  };

/**
 * Factory function which creates a producer to update the current list of log ids (continuation tokens) and pad them
 * with empty strings to represent lines in the result set that have not yet been loaded from the server. Note that we
 * have access to a reliable total matchCount, but we don't have access to reliable directional matchCounts. Therefore,
 * the results for centered searches will have to overestimate until one or more termini is found.
 *
 * Example: We receive a centered search request with 1,000 total results. We start with an ascending anchor request and
 * a descending anchor request for 200 logs each. Both requests return 200 results, and also report that the end of the
 * result set has not yet been reached. There are 600 results missing, and we don't know how many are before the results
 * we have already loaded or how many are after. For now, the results are padded by 600 in each direction. The user
 * scrolls down a bit looking for more recent results, and triggers a continuation request for more ascending results.
 * This time the request returns only 100 results of the 200 requested and it reports that the end of the result set has
 * been reached. At this point the model is recalculated to represent the result set accurately, with all 500 of the yet
 * unloaded logs represented in the opposite direction.
 *
 * Note: this example assumes that sortOrder is {@see SortOrder.MOST_RECENT_AT_BOTTOM}
 *
 * @param blockCount          The total number of request blocks currently modeled on the client
 * @param includesFirstLog    True if the first log (top of screen, left side of array) in the result set is loaded
 * @param includesLastLog     True if the last log (bottom of screen, right side of array) in the result set is loaded
 * @param matchCount          The total number of logs in the result set whether loaded or not.
 *
 * @return              producer function to update the current ids based on the enclosed parameters.
 */
export const padLogIdList =
  (blockCount: number, includesFirstLog: boolean, includesLastLog: boolean, matchCount: number): Producer =>
  (currentIds: Array<string>): void => {
    // Calculate the number of results that have not yet populated in the cache to represent with placeholders in the
    // result set. As an effective count, we use the lesser of the actual matchCount, and the number that would be
    // needed if every block currently represented by the set of requests were a full block.
    const effectiveCount = Math.min(matchCount, blockCount * K.NUM_INITIAL_LOG_ENTRIES);
    const remainingMatchCount = Math.max(0, effectiveCount - currentIds.length);
    const placeholders = new Array(remainingMatchCount).fill('');

    // Left pad the list up to remainingMatchCount unless the leftmost log is already loaded.
    if (remainingMatchCount > 0 && !includesFirstLog) {
      currentIds.unshift(...placeholders);
    }

    // Right pad the list up to remainingMatchCount unless the rightmost log is already loaded.
    if (remainingMatchCount > 0 && !includesLastLog) {
      currentIds.push(...placeholders);
    }
  };
